const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL;
const DIRECTUS_TOKEN = process.env.REACT_APP_DIRECTUS_TOKEN;
const DIRECTUS_WRITE_TOKEN = process.env.REACT_APP_DIRECTUS_WRITE_TOKEN || DIRECTUS_TOKEN;

const directusFetch = async (path) => {
    const response = await fetch(`${DIRECTUS_URL}${path}`, {
        headers: {
            Authorization: `Bearer ${DIRECTUS_TOKEN}`,
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        throw new Error(`Directus error: ${response.statusText}`);
    }
    return response.json();
};

/**
 * Fetches the Finops finance record for a given booking ID.
 * Returns the first matching record from the FinanceRequests collection.
 * @param {string} bookingId
 * @returns {Promise<object|null>}
 */
export const fetchFinanceStatus = async (bookingId) => {
    if (!bookingId) return null;
    try {
        const filter = JSON.stringify({
            _and: [
                { BookingId: { _eq: bookingId } },
                { IsDeleted: { _eq: false } },
            ],
        });
        const result = await directusFetch(
            `/items/FinanceRequests?filter=${encodeURIComponent(filter)}&limit=1&fields=*`
        );
        const data = result.data;
        return data && data.length > 0 ? data[0] : null;
    } catch (error) {
        console.error("DirectusService: fetchFinanceStatus error:", error);
        return null;
    }
};

/**
 * Checks if a booking's Finops status allows marking as delivered.
 * Returns true only if status is "Disbursed" or "Agreement Signed".
 * @param {string} bookingId
 * @returns {Promise<boolean>}
 */
export const isFinopsStatusDeliverable = async (bookingId) => {
    const record = await fetchFinanceStatus(bookingId);
    if (!record) return false;
    const deliverableStatuses = ["Disbursed", "Agreement Signed"];
    return deliverableStatuses.includes(record.Status);
};

/**
 * Fetches the Finance Penetration Rate for a given showroom/location.
 * Calculates: (Disbursed cases / Total active cases) * 100
 * @param {string} location - The showroom/location name (e.g., "Telibagh")
 * @returns {Promise<string>} - Penetration rate as a percentage string (e.g., "42.5")
 */
export const fetchFinancePenetration = async (location) => {
    if (!location) return "0";
    try {
        const baseFilter = {
            _and: [
                { IsDeleted: { _eq: false } },
                { Showroom: { _eq: location } },
            ],
        };

        const result = await directusFetch(
            `/items/FinanceRequests?filter=${encodeURIComponent(JSON.stringify(baseFilter))}&limit=-1&fields=Status,IsActive`
        );

        const data = result.data || [];
        const total = data.length;
        if (total === 0) return "0";

        const disbursed = data.filter((c) => c.Status === "Disbursed").length;
        return ((disbursed / total) * 100).toFixed(1);
    } catch (error) {
        return "0";
    }
};

/**
 * Updates ShowStock column to 1 for multiple VINs in the carstock table.
 * This unhides the selected stock items.
 * @param {Array<string>} vins - Array of VIN numbers to unhide
 * @returns {Promise<object>} - Result object with success status and message
 */
export const updateShowStock = async (vins) => {
    if (!vins || vins.length === 0) {
        return { success: false, message: "No VINs provided" };
    }

    try {
        const response = await fetch(`${DIRECTUS_URL}/items/carstock`, {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${DIRECTUS_TOKEN}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ 
                query: {
                    filter: {
                        win_num: { _in: vins }
                    }
                },
                data: { ShowStock: 1 }
            }),
        });

        if (!response.ok) {
            throw new Error(`Directus update error: ${response.statusText}`);
        }

        const result = await response.json();
        
        return {
            success: true,
            message: `Successfully updated ${vins.length} stock item(s)`,
            data: result.data
        };
    } catch (error) {
        return {
            success: false,
            message: error.message || "Failed to update stock visibility"
        };
    }
};

/**
 * Fetches the insurance case record for a given booking ID.
 * Returns the insurance case data from the InsuranceCases collection.
 * @param {string} bookingId
 * @returns {Promise<object|null>}
 */
export const fetchInsuranceStatus = async (bookingId) => {
    if (!bookingId) return null;
    try {
        const filter = JSON.stringify({
            _and: [
                { BookingId: { _eq: bookingId } },
                { IsDeleted: { _eq: false } },
            ],
        });
        const result = await directusFetch(
            `/items/InsuranceCases?filter=${encodeURIComponent(filter)}&limit=1&fields=*`
        );
        const data = result.data;
        return data && data.length > 0 ? data[0] : null;
    } catch (error) {
        console.error("DirectusService: fetchInsuranceStatus error:", error);
        return null;
    }
};

/**
 * Checks if insurance documents are uploaded for a booking.
 * Returns true if insurance case exists and has documents uploaded.
 * @param {string} bookingId
 * @returns {Promise<boolean>}
 */
export const isInsuranceDocumentsUploaded = async (bookingId) => {
    const record = await fetchInsuranceStatus(bookingId);
    if (!record) return false;
    // Check if insurance documents are uploaded (adjust field name as per your schema)
    // Assuming there's a field like InsuranceDocumentURL or Status
    return !!(record.InsuranceDocumentURL || record.PolicyDocument);
};

/**
 * Fetches the RTO case record for a given booking ID.
 * Returns the RTO case data from the RMOCases collection.
 * @param {string} bookingId
 * @returns {Promise<object|null>}
 */
export const fetchRTOStatus = async (bookingId) => {
    if (!bookingId) return null;
    try {
        const filter = JSON.stringify({
            _and: [
                { BookingId: { _eq: bookingId } },
                { IsDeleted: { _eq: false } },
            ],
        });
        const result = await directusFetch(
            `/items/RMOCases?filter=${encodeURIComponent(filter)}&limit=1&fields=*`
        );
        const data = result.data;
        return data && data.length > 0 ? data[0] : null;
    } catch (error) {
        console.error("DirectusService: fetchRTOStatus error:", error);
        return null;
    }
};

/**
 * Fetches all active number choices.
 * @returns {Promise<Array>}
 */
export const fetchNumberChoices = async () => {
    try {
        const filter = JSON.stringify({
            IsActive: { _eq: true },
        });
        const result = await directusFetch(
            `/items/NumberChoices?filter=${encodeURIComponent(filter)}&limit=-1&fields=*`
        );
        return result.data || [];
    } catch (error) {
        console.error("DirectusService: fetchNumberChoices error:", error);
        return [];
    }
};

/**
 * Fetches all active annual schemes.
 * @returns {Promise<Array>}
 */
export const fetchAnnualSchemes = async () => {
    try {
        const filter = JSON.stringify({
            IsActive: { _eq: true },
        });
        const result = await directusFetch(
            `/items/AnnualSchemes?filter=${encodeURIComponent(filter)}&limit=-1&fields=*`
        );
        return result.data || [];
    } catch (error) {
        console.error("DirectusService: fetchAnnualSchemes error:", error);
        return [];
    }
};

/**
 * Updates an insurance case record by booking ID directly via Directus PATCH.
 * Requires REACT_APP_DIRECTUS_WRITE_TOKEN to have write access to InsuranceCases.
 * @param {string} bookingId
 * @param {object} data - Fields to update (e.g., EngineNumber, ExShowroomPrice)
 */
export const updateInsuranceCaseData = async (bookingId, data) => {
    if (!bookingId) return null;
    try {
        const filter = JSON.stringify({
            _and: [{ BookingId: { _eq: bookingId } }, { IsDeleted: { _eq: false } }],
        });
        const searchResult = await fetch(
            `${DIRECTUS_URL}/items/InsuranceCases?filter=${encodeURIComponent(filter)}&limit=1&fields=Id,InsuranceUUID`,
            { headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" } }
        );
        const searchJson = await searchResult.json();
        const record = searchJson.data?.[0];
        if (!record) { console.warn("InsuranceCases: no record found for", bookingId); return null; }

        // Use numeric Id as Directus primary key
        const pk = record.Id || record.InsuranceUUID;
        const response = await fetch(`${DIRECTUS_URL}/items/InsuranceCases/${pk}`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.text().catch(() => response.statusText);
            console.warn(`InsuranceCases PATCH failed (${response.status}):`, err);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("updateInsuranceCaseData error:", error);
        return null;
    }
};

/**
 * Updates an RMO (RTO) case record by booking ID directly via Directus PATCH.
 * Requires REACT_APP_DIRECTUS_WRITE_TOKEN to have write access to RMOCases.
 * @param {string} bookingId
 * @param {object} data - Fields to update (e.g., EngineNumber)
 */
export const updateRMOCaseData = async (bookingId, data) => {
    if (!bookingId) return null;
    try {
        const filter = JSON.stringify({
            _and: [{ BookingId: { _eq: bookingId } }, { IsDeleted: { _eq: false } }],
        });
        const searchResult = await fetch(
            `${DIRECTUS_URL}/items/RMOCases?filter=${encodeURIComponent(filter)}&limit=1&fields=Id,CaseUUID`,
            { headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" } }
        );
        const searchJson = await searchResult.json();
        const record = searchJson.data?.[0];
        if (!record) { console.warn("RMOCases: no record found for", bookingId); return null; }

        // Use numeric Id as Directus primary key
        const pk = record.Id || record.CaseUUID;
        const response = await fetch(`${DIRECTUS_URL}/items/RMOCases/${pk}`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.text().catch(() => response.statusText);
            console.warn(`RMOCases PATCH failed (${response.status}):`, err);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("updateRMOCaseData error:", error);
        return null;
    }
};

/**
 * Updates a booking record's NumberChoice and SchemeName directly via Directus PATCH.
 * Requires REACT_APP_DIRECTUS_WRITE_TOKEN to have write access to the booking collection.
 * @param {string} bookingId - The BookingID UUID (capital D field)
 * @param {object} data - { NumberChoice, SchemeName }
 */
export const updateBookingReadyForDelivery = async (bookingId, data) => {
    if (!bookingId) return null;
    try {
        // booking table: UUID field is "BookingID" (capital D), PK is "booking_id" (numeric)
        const filter = JSON.stringify({ BookingID: { _eq: bookingId } });
        const searchResult = await fetch(
            `${DIRECTUS_URL}/items/booking?filter=${encodeURIComponent(filter)}&limit=1&fields=booking_id`,
            { headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" } }
        );
        const searchJson = await searchResult.json();
        console.log("booking lookup response:", searchJson);
        const record = searchJson.data?.[0];
        if (!record) { console.warn("booking: no record found for", bookingId); return null; }

        const pk = record.booking_id;
        console.log("booking PATCH pk:", pk, "data:", data);
        const response = await fetch(`${DIRECTUS_URL}/items/booking/${pk}`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.text().catch(() => response.statusText);
            console.warn(`booking PATCH failed (${response.status}):`, err);
            return null;
        }
        return await response.json();
    } catch (error) {
        console.error("updateBookingReadyForDelivery error:", error);
        return null;
    }
};

export default { fetchFinanceStatus, isFinopsStatusDeliverable, fetchFinancePenetration, updateShowStock, fetchInsuranceStatus, isInsuranceDocumentsUploaded, fetchRTOStatus, fetchNumberChoices, fetchAnnualSchemes, updateBookingReadyForDelivery, updateInsuranceCaseData, updateRMOCaseData };

/**
 * Updates VIN number in all downstream tables after car allocation.
 * Patches FinanceRequests, CustomerPanelCases, InsuranceCases, RMOCases with the allocated VIN.
 * @param {string} bookingId - BookingID UUID
 * @param {object} carData - { vinNumber, vehicleModel, vehicleVariant, vehicleColor, yom }
 */
export const updateAllocatedCarInTables = async (bookingId, carData) => {
    if (!bookingId || !carData?.vinNumber) return;

    const { vinNumber, vehicleModel, vehicleVariant, vehicleColor, yom } = carData;
    const headers = { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" };

    const patchByBookingId = async (collection, filterField, pkField, patchData) => {
        try {
            // Don't filter by IsDeleted — different tables may use different types (bool vs int)
            const filter = JSON.stringify({ [filterField]: { _eq: bookingId } });
            const res = await fetch(`${DIRECTUS_URL}/items/${collection}?filter=${encodeURIComponent(filter)}&limit=1&fields=${pkField}`, { headers });
            const json = await res.json();
            console.log(`${collection} lookup result:`, json);
            const record = json.data?.[0];
            if (!record) { console.warn(`${collection}: no record for`, bookingId); return; }
            const patchRes = await fetch(`${DIRECTUS_URL}/items/${collection}/${record[pkField]}`, {
                method: "PATCH", headers, body: JSON.stringify(patchData),
            });
            const patchBody = await patchRes.text();
            console.log(`${collection} PATCH status:`, patchRes.status, patchBody);
        } catch (err) {
            console.error(`${collection} update error:`, err);
        }
    };

    await Promise.all([
        patchByBookingId("InsuranceCases", "BookingId", "Id", {
            VinNumber: vinNumber,
            VehicleModel: vehicleModel,
            VehicleVariant: vehicleVariant,
            VehicleColor: vehicleColor,
        }),
        patchByBookingId("RMOCases", "BookingId", "Id", {
            VinNumber: vinNumber,
            VehicleModel: vehicleModel,
            VehicleVariant: vehicleVariant,
            VehicleColor: vehicleColor,
        }),
        patchByBookingId("FinanceRequests", "BookingId", "Id", {
            VIN: vinNumber,
            CarModel: vehicleModel,
            CarVariant: vehicleVariant,
            CarColour: vehicleColor,
        }),
        // CustomerPanelCases uses BookingId as Wings booking number — try multiple ID formats
        (async () => {
            try {
                // Get booking record to find WingsId / receipt_num / booking_id
                const bookingFilter = JSON.stringify({ BookingID: { _eq: bookingId } });
                const bookingRes = await fetch(
                    `${DIRECTUS_URL}/items/booking?filter=${encodeURIComponent(bookingFilter)}&limit=1&fields=booking_id,WingsId,receipt_num`,
                    { headers }
                );
                const bookingJson = await bookingRes.json();
                console.log("CustomerPanelCases booking lookup:", bookingJson);
                const booking = bookingJson.data?.[0];
                if (!booking) { console.warn("CustomerPanelCases: booking not found for", bookingId); return; }

                // CustomerPanelCases.BookingId is the numeric booking_id
                const possibleIds = [Number(booking.booking_id), booking.WingsId, booking.receipt_num].filter(Boolean);
                let cpRecord = null;
                for (const id of possibleIds) {
                    const cpFilter = JSON.stringify({ BookingId: { _eq: id } });
                    const cpRes = await fetch(
                        `${DIRECTUS_URL}/items/CustomerPanelCases?filter=${encodeURIComponent(cpFilter)}&limit=1&fields=Id`,
                        { headers }
                    );
                    const cpJson = await cpRes.json();
                    console.log(`CustomerPanelCases lookup BookingId="${id}":`, cpJson);
                    if (cpJson.data?.[0]) { cpRecord = cpJson.data[0]; break; }
                }

                if (!cpRecord) { console.warn("CustomerPanelCases: no record found for booking", bookingId); return; }

                const patchRes = await fetch(`${DIRECTUS_URL}/items/CustomerPanelCases/${cpRecord.Id}`, {
                    method: "PATCH",
                    headers,
                    body: JSON.stringify({ Model: vehicleModel, Variant: vehicleVariant, Colour: vehicleColor }),
                });
                console.log("CustomerPanelCases PATCH status:", patchRes.status);
            } catch (err) {
                console.error("CustomerPanelCases update error:", err);
            }
        })(),
        // CashFlo uses StockOneBookingId (WingsId) as lookup key
        (async () => {
            try {
                const bookingFilter = JSON.stringify({ BookingID: { _eq: bookingId } });
                const bookingRes = await fetch(
                    `${DIRECTUS_URL}/items/booking?filter=${encodeURIComponent(bookingFilter)}&limit=1&fields=WingsId`,
                    { headers }
                );
                const bookingJson = await bookingRes.json();
                const booking = bookingJson.data?.[0];
                if (!booking?.WingsId) { console.warn("CashFlo: no WingsId for booking", bookingId); return; }

                const cfFilter = JSON.stringify({ StockOneBookingId: { _eq: booking.WingsId } });
                const cfRes = await fetch(
                    `${DIRECTUS_URL}/items/CashFlo?filter=${encodeURIComponent(cfFilter)}&limit=1&fields=Id`,
                    { headers }
                );
                const cfJson = await cfRes.json();
                console.log("CashFlo lookup:", cfJson);
                const cfRecord = cfJson.data?.[0];
                if (!cfRecord) { console.warn("CashFlo: no record for WingsId", booking.WingsId); return; }

                const patchRes = await fetch(`${DIRECTUS_URL}/items/CashFlo/${cfRecord.Id}`, {
                    method: "PATCH",
                    headers,
                    body: JSON.stringify({ Model: vehicleModel, Variant: vehicleVariant, Colour: vehicleColor }),
                });
                console.log("CashFlo PATCH status:", patchRes.status);
            } catch (err) {
                console.error("CashFlo update error:", err);
            }
        })(),
    ]);
};


/**
 * Marks a BookingTransfer record as completed by SRM.
 * @param {string} oldWingsId - The OldWingsId to look up the transfer record
 */
export const markBookingTransferCompleted = async (oldWingsId) => {
    if (!oldWingsId) return;
    try {
        const filter = JSON.stringify({ OldWingsId: { _eq: oldWingsId }, IsDeleted: { _eq: false } });
        const res = await fetch(
            `${DIRECTUS_URL}/items/BookingTransfer?filter=${encodeURIComponent(filter)}&limit=1&fields=Id`,
            { headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" } }
        );
        const json = await res.json();
        const record = json.data?.[0];
        if (!record) { console.warn("BookingTransfer: no record for WingsId", oldWingsId); return; }

        await fetch(`${DIRECTUS_URL}/items/BookingTransfer/${record.Id}`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${DIRECTUS_WRITE_TOKEN}`, "Content-Type": "application/json" },
            body: JSON.stringify({ ShowroomManagerStatus: "Completed" }),
        });
        console.log("BookingTransfer marked as Completed for", oldWingsId);
    } catch (err) {
        console.error("markBookingTransferCompleted error:", err);
    }
};

/**
 * Fetches carstock details for a list of VIN numbers.
 * Returns a map of { win_num -> { model_name, variant, colour, fueltype } }
 * @param {string[]} vins
 * @returns {Promise<Map>}
 */
export const fetchCarStockByVins = async (vins) => {
    if (!vins || vins.length === 0) return new Map();
    try {
        const filter = JSON.stringify({ win_num: { _in: vins } });
        const result = await fetch(
            `${DIRECTUS_URL}/items/carstock?filter=${encodeURIComponent(filter)}&limit=-1&fields=win_num,model_name,variant,colour,fueltype`,
            { headers: { Authorization: `Bearer ${DIRECTUS_TOKEN}`, "Content-Type": "application/json" } }
        );
        const json = await result.json();
        const map = new Map();
        (json.data || []).forEach(car => { if (car.win_num) map.set(car.win_num, car); });
        return map;
    } catch (err) {
        console.error("fetchCarStockByVins error:", err);
        return new Map();
    }
};
